import React, { useRef } from "react"
import { Flex } from "@chakra-ui/layout"
import type { CMSPageProps } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import { cmsPageClick } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import type { FeaturedProducts } from "types/cms"
import { useLandingPageContext } from "packages/acquisition/components/chakra-components/LandingPage/LandingPageProvider"
import { Link } from "components/Link"
import { cleanTitle } from "components/Contentful/RichTextRenderer/RichTextRenderer.utils"
import { getColour } from "utils/getColour"
import {
  Headline,
  InnerContainer,
  OuterContainer,
  ProductsContainer,
  ShopLinkText,
} from "../styles"
import { ProductCard } from "../ProductCard"
import { scrollBarCss } from "../utils"

import { CarouselModule } from "packages/ui-shared/components/CarouselModule"
import { useSliderWithThreeItems } from "packages/ui-shared/components/CarouselModule/hooks/useCarouselProps"

type Props = CMSPageProps & FeaturedProducts

export const FeaturedProductsWithLink: React.FC<Props> = ({
  title,
  variants,
  link,
  trackingModule,
  backgroundColour,
}: Props) => {
  const { ctVariants } = useLandingPageContext()
  const { modulePosition } = trackingModule

  const ref = useRef(null)

  const slidesPerView = 3

  // Fix swiper issue showing last product as first
  const items = variants
    .map((variant) => ctVariants?.find((ctVar) => ctVar.sku === variant.sku))
    .filter(Boolean)

  if (items.length && slidesPerView > 3) {
    const firstElement = items.shift()
    if (firstElement) {
      items.push(firstElement)
    }
  }

  const hasOneCompareTo = items?.some((variant) => {
    return !!variants.some((_variant) => {
      if (_variant.sku === variant?.sku && _variant.compareTo) return true
      return false
    })
  })

  const handleClick = () => {
    cmsPageClick({
      ...trackingModule,
      moduleContentUrl: link?.url,
    })
  }

  const sliderWithThreeItemsProps = useSliderWithThreeItems({
    itemsLength: items.length,
  })

  return (
    <OuterContainer
      ref={ref}
      backgroundColor={getColour(backgroundColour)}
      maxW={"100%"}
    >
      <InnerContainer gap="2.5rem">
        <Flex
          justify="space-between"
          alignItems="center"
          w="full"
          px={{
            base: "1.5rem",
            md: "5rem",
            lg: "7.5rem",
          }}
        >
          <Headline
            variant="apercu"
            as={modulePosition === 1 ? "h1" : "h2"}
            textAlign="left"
            width="max-content"
            id={cleanTitle(title)}
          >
            {title}
          </Headline>

          {link && (
            <ShopLinkText textAlign="right">
              <Link
                onClick={handleClick}
                href={link?.url}
                variant="acuminMedium"
                fontWeight="500"
              >
                {link?.title}
              </Link>
            </ShopLinkText>
          )}
        </Flex>
        <Flex
          display={{ base: "none", lg: "flex" }}
          px="7.5rem"
          position="relative"
        >
          <CarouselModule
            tracking={null}
            carouselProps={sliderWithThreeItemsProps}
          >
            {items.map((variant) => {
              const contentfulVariant = variants.find(
                (_variant) => _variant.sku === variant?.sku
              )
              const { ribbon, compareTo } = contentfulVariant ?? {}

              return (
                variant && (
                  <Flex w="full" h="full">
                    <ProductCard
                      key={`${variant.id}-${variant.sku}`}
                      variant={variant}
                      ribbon={ribbon}
                      compareTo={compareTo}
                      hasOneCompareTo={hasOneCompareTo}
                      trackingModule={trackingModule}
                    />
                  </Flex>
                )
              )
            })}
          </CarouselModule>
        </Flex>
        <ProductsContainer
          display={{ base: "flex", lg: "none" }}
          sx={scrollBarCss}
        >
          <Flex
            gap={{ base: "1.5rem", lg: "2.813rem" }}
            w="max-content"
            m="0 auto"
          >
            {items.map((variant) => {
              const contentfulVariant = variants.find(
                (_variant) => _variant.sku === variant?.sku
              )

              const { ribbon, compareTo } = contentfulVariant ?? {}

              return (
                variant && (
                  <ProductCard
                    key={variant.sku}
                    variant={variant}
                    ribbon={ribbon}
                    compareTo={compareTo}
                    hasOneCompareTo={hasOneCompareTo}
                    trackingModule={trackingModule}
                  />
                )
              )
            })}
          </Flex>
        </ProductsContainer>
      </InnerContainer>
    </OuterContainer>
  )
}
